import { estimate1RM } from '@fitness/core';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, CloudOff, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { ErrorBanner } from '@/components/ErrorBanner';
import { PageHeader } from '@/components/PageHeader';
import { Spinner } from '@/components/Spinner';
import {
  SessionExerciseTable,
  groupByExercise,
  type DetailSet,
} from '@/components/workout/SessionExerciseTable';
import { SyncBadge } from '@/components/workout/SyncBadge';
import {
  formatDayTime,
  formatDuration,
  formatVolume,
  sessionTitle,
} from '@/components/workout/format';
import { describeError } from '@/lib/api';
import { purgeLocalSession } from '@/lib/offline/session';
import {
  useDeleteWorkout,
  useLocalSession,
  useLocalSets,
  useWorkoutDetail,
} from '@/lib/queries/workouts';

export const Route = createFileRoute('/_app/train/history/$clientId')({
  component: SessionDetailPage,
});

/** El contrato admite `reps: 0`; la fórmula de Epley, no. */
function oneRm(weightKg: number, reps: number): number | null {
  if (!Number.isInteger(reps) || reps < 1 || weightKg <= 0) return null;
  return estimate1RM(weightKg, reps);
}

function SessionDetailPage() {
  const { clientId } = Route.useParams();
  const navigate = useNavigate();
  const remote = useWorkoutDetail(clientId);
  const localSession = useLocalSession(clientId);
  const localSets = useLocalSets(clientId);
  const remove = useDeleteWorkout();
  const [confirming, setConfirming] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  /** Manda el servidor si responde; si no (o sin red), la copia local. */
  const detail = useMemo(() => {
    if (remote.data !== undefined) {
      const sets: DetailSet[] = (remote.data.sets ?? []).map((set) => ({
        clientId: set.clientId,
        exerciseId: set.exercise.id,
        exerciseName: set.exercise.nameEs,
        setNumber: set.setNumber,
        setType: set.setType,
        reps: set.reps,
        weightKg: set.weightKg,
        rpe: set.rpe,
        estimated1RmKg: set.estimated1RmKg,
        unilateral: set.exercise.unilateral,
        durationSeconds: set.durationSeconds,
        distanceM: set.distanceM,
      }));
      return {
        title: sessionTitle(remote.data.routineName, remote.data.dayName),
        startedAt: remote.data.startedAt,
        finishedAt: remote.data.finishedAt,
        sets,
        synced: true,
      };
    }
    if (localSession == null || localSets === undefined) return null;
    const sets: DetailSet[] = localSets.map((set) => ({
      clientId: set.clientId,
      exerciseId: set.exerciseId,
      exerciseName: set.exerciseName,
      setNumber: set.setNumber,
      setType: set.setType,
      reps: set.reps,
      weightKg: set.weightKg,
      rpe: set.rpe,
      estimated1RmKg: oneRm(set.weightKg, set.reps),
      unilateral: set.unilateral ?? false,
      durationSeconds: set.durationSeconds ?? null,
      distanceM: set.distanceM ?? null,
    }));
    return {
      title: sessionTitle(localSession.routineName, localSession.dayName),
      startedAt: localSession.startedAt,
      finishedAt: localSession.finishedAt,
      sets,
      synced: localSession.syncedAt !== null,
    };
  }, [remote.data, localSession, localSets]);

  async function handleDelete(): Promise<void> {
    setLocalError(null);
    // Una sesión que el servidor no conoce se borra sólo del dispositivo: pedir
    // el borrado remoto fallaría sin red y dejaría el botón inservible.
    if (detail?.synced === false) {
      try {
        await purgeLocalSession(clientId);
        await navigate({ to: '/train/history', replace: true });
      } catch (cause) {
        setLocalError(describeError(cause));
      }
      return;
    }
    remove.mutate(clientId, {
      onSuccess: () => {
        void navigate({ to: '/train/history', replace: true });
      },
    });
  }

  const loading = remote.isPending && (localSession === undefined || localSets === undefined);

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner size="lg" />
      </div>
    );
  }

  if (detail === null) {
    return (
      <>
        <PageHeader title="Entrenamiento" />
        <ErrorBanner
          title="No hemos encontrado este entrenamiento"
          message={remote.isError ? describeError(remote.error) : 'Puede que se haya borrado.'}
          onRetry={remote.isError ? () => void remote.refetch() : undefined}
        />
      </>
    );
  }

  const groups = groupByExercise(detail.sets);
  const totalVolume = groups.reduce((total, group) => total + group.volumeKg, 0);
  const started = new Date(detail.startedAt).getTime();
  const ended = detail.finishedAt === null ? null : new Date(detail.finishedAt).getTime();

  return (
    <>
      <PageHeader
        title={detail.title}
        subtitle={formatDayTime(detail.startedAt)}
        action={
          <Link
            to="/train/history"
            aria-label="Volver al historial"
            className="flex size-11 items-center justify-center rounded-xl border border-line text-ink-muted transition-colors hover:bg-surface-raised active:bg-surface-raised"
          >
            <ArrowLeft className="size-5" aria-hidden="true" />
          </Link>
        }
      />

      <div className="flex flex-col gap-4">
        {detail.synced ? null : (
          <p className="flex items-center gap-2 rounded-xl border border-line bg-surface-overlay px-3 py-2 text-xs text-ink-muted">
            <CloudOff className="size-4 shrink-0" aria-hidden="true" />
            Este entrenamiento todavía no está en el servidor.
          </p>
        )}
        <SyncBadge />

        <Card title="Resumen">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-xl bg-surface px-2 py-2">
              <p className="text-lg font-bold text-ink tabular-nums">{detail.sets.length}</p>
              <p className="text-xs text-ink-muted">Series</p>
            </div>
            <div className="rounded-xl bg-surface px-2 py-2">
              <p className="text-lg font-bold text-ink tabular-nums">{formatVolume(totalVolume)}</p>
              <p className="text-xs text-ink-muted">Volumen</p>
            </div>
            <div className="rounded-xl bg-surface px-2 py-2">
              <p className="text-lg font-bold text-ink tabular-nums">
                {ended === null ? 'En curso' : formatDuration(ended - started)}
              </p>
              <p className="text-xs text-ink-muted">Duración</p>
            </div>
          </div>
        </Card>

        {groups.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-line bg-surface-raised px-4 py-8 text-center text-sm text-ink-muted">
            Esta sesión no tiene ninguna serie registrada.
          </p>
        ) : (
          groups.map((group) => <SessionExerciseTable key={group.exerciseId} group={group} />)
        )}

        {remove.isError ? (
          <ErrorBanner title="No se ha podido borrar" error={remove.error} />
        ) : null}
        {localError !== null ? <ErrorBanner message={localError} /> : null}

        {confirming ? (
          <Card
            title="¿Borrar este entrenamiento?"
            description="Se borrarán también todas sus series. No se puede deshacer."
          >
            <div className="flex gap-2">
              <Button
                variant="danger"
                block
                loading={remove.isPending}
                onClick={() => void handleDelete()}
              >
                Sí, borrar
              </Button>
              <Button variant="secondary" block onClick={() => setConfirming(false)}>
                Cancelar
              </Button>
            </div>
          </Card>
        ) : (
          <Button
            variant="ghost"
            block
            icon={<Trash2 className="size-4" />}
            onClick={() => setConfirming(true)}
          >
            Borrar entrenamiento
          </Button>
        )}
      </div>
    </>
  );
}
